import { gql, useMutation, useQuery } from '@apollo/client'

import {
  ArtistUpdateDigitalMembershipCardMutation,
  ArtistUpdateDigitalMembershipCardMutationVariables,
  GetFavoriteMemberCardDesignQuery,
  GetFavoriteMemberCardDesignQueryVariables,
} from '@/clients/utoniq-core/schema'
import { config } from '@/config'
import {
  useMessage,
  useRouter,
  useTranslation,
  useUserTmpUploader,
} from '@/core'
import { makeViewModel } from '@/core/architecture'
import { useCurrentUser } from '@/core/auth'
import { Camera } from '@/core/native'
import { createUuid } from '@/lib/uuid'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { user } = useCurrentUser()
  const { router, routerPages, generatePath } =
    useRouter<{ favoriteMemberId: string }>()
  const { showError, showMessage } = useMessage()
  const { t } = useTranslation('consoleDigitalMembershipDefaultCardDesignEdit')
  const { getImage } = Camera
  const { upload, progress } = useUserTmpUploader(user?.uid || '')

  const favoriteMemberId = router.query.favoriteMemberId

  const { data, loading, error } = useQuery<
    GetFavoriteMemberCardDesignQuery,
    GetFavoriteMemberCardDesignQueryVariables
  >(QUERY, {
    variables: {
      inputs: {
        favoriteMemberId: favoriteMemberId,
      },
    },
  })

  const [updateFavoriteMemberDigitalMembershipCard, { loading: updating }] =
    useMutation<
      ArtistUpdateDigitalMembershipCardMutation,
      ArtistUpdateDigitalMembershipCardMutationVariables
    >(UPDATE_DIGITAL_MEMBERSHIP_CARD, {
      onCompleted() {
        showMessage(t('editSuccessful'), 'success')
        router.goBack(generatePath(routerPages.ConsoleDigitalMembershipCard))
      },
    })

  const onDrop = async () => {
    const file = await getImage(config.upload.mimeTypes.image)
    if (file.size > config.upload.limit.image) {
      throw new Error('uploadErrorMaximumSize')
    }
    return file
  }

  return {
    state: {
      artistId: user?.uid ?? '',
      favoriteMember: data?.artistFavoriteMembers?.[0] ?? null,
      progress,
      loading,
      membershipCard:
        data?.artistFavoriteMembers?.[0]?.digitalMembershipCard ?? null,
    },
    actions: {
      async onSubmit({ cardDesignPath, textColor, displayAreaColor }) {
        await updateFavoriteMemberDigitalMembershipCard({
          variables: {
            inputs: {
              favoriteMemberId: favoriteMemberId,
              cardDesignPath,
              textColor,
              displayAreaColor,
            },
          },
        })
      },
      async onDrop() {
        const file = await onDrop().catch(error => {
          showError(new Error(t([error.message, 'interruptedUpload'])))
        })
        if (!file) {
          return
        }
        const { tmpPath, fileUrl } = await upload(file, false, true)
        const uuid = createUuid()
        return {
          id: uuid,
          resourceName: uuid,
          imageUrl: fileUrl,
          imageUploadPath: tmpPath,
        }
      },
    },
  }
})

const QUERY = gql`
  query GetFavoriteMemberCardDesign($inputs: ArtistFavoriteMembersInput!) {
    artistFavoriteMembers(inputs: $inputs) {
      id
      name
      digitalMembershipCard {
        cardDesignPath
        displayAreaColor
        textColor
      }
    }
  }
`

const UPDATE_DIGITAL_MEMBERSHIP_CARD = gql`
  mutation ArtistUpdateFavoriteMemberDigitalMembershipCard(
    $inputs: ArtistUpdateDigitalMembershipCardInput!
  ) {
    artistUpdateDigitalMembershipCard(inputs: $inputs) {
      artistFavoriteMembers {
        id
        name
        orderNumber
        digitalMembershipCard {
          cardDesignPath
          displayAreaColor
          textColor
        }
      }
    }
  }
`
